<template>
  <div class="trackers-con pt-3">
    <h5 class="mt-3 mb-5">
      TRACKER COMBINATION: {{ convertStringToTitle(trackerCombination) }}
    </h5>
    <div class="wrapper-edit">
      <b-table
        v-if="!isImportingPlacementLevelTrackers"
        ref="trackers"
        class="card-table table-vcenter text-nowrap content-tracker-table"
        tbody-tr-class="trackers-table-row"
        :striped="false"
        :bordered="false"
        :outlined="false"
        :hover="true"
        :items="displayedTrackerItems"
        :fields="getHeaders"
        :sort-desc="false"
        show-empty
        data-test="trackers-list"
        responsive
        sticky-header="450px"
        @row-clicked="rowActions().editTrackers"
      >
        <template v-slot:table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="`width: ${field.width ? field.width : 'auto'}`"
          >
        </template>
        <template v-slot:cell(impressionTracker)="data">
          <status-btn
            v-if="data.value"
            tracker-type="impressionTracker"
            :trackers-info="[...data.value]"
            :scope="'CONTENT_ITEM'"
            :border="true"
          />
        </template>
        <template v-slot:cell(clickTracker)="data">
          <status-btn
            v-if="data.value"
            tracker-type="clickTracker"
            :trackers-info="[...data.value]"
            :scope="'CONTENT_ITEM'"
            :border="true"
          />
        </template>
        <template v-slot:cell(postClickTracker)="data">
          <status-btn
            v-if="data.value"
            tracker-type="postClickTracker"
            :trackers-info="[...data.value]"
            :scope="'CONTENT_ITEM'"
            :border="true"
          />
        </template>
        <template v-slot:cell(thirdPartyTags)="data">
          <status-btn
            v-if="getThirdPartyTagsInfo(data.item).length"
            tracker-type="thirdPartyTags"
            :trackers-info="getThirdPartyTagsInfo(data.item)"
            :scope="'CONTENT_ITEM'"
            :display-count="true"
            :border="true"
            :disabled="true"
          />
        </template>
        <template v-slot:cell(actions)="data">
          <div class="actions">
            <b-button
              size="md"
              variant="transparant"
              @click="rowActions().editTrackers(data.item)"
            >
              <i class="fas fa-edit" />
            </b-button>
            <b-button
              class="ml-3"
              size="md"
              variant="transparant"
              @click="rowActions().deleteTrackers(data)"
            >
              <i class="fas fa-trash-alt trash-alt" />
            </b-button>
          </div>
        </template>

        <!-- Edit here on button click -->
        <template v-slot:row-details="data">
          <edit-panel
            :item="data.item"
            :tracker-combination="trackerCombination"
          />
        </template>

        <template v-slot:empty>
          <p>
            This campaign doesn't have any trackers configured
          </p>
        </template>
        <template v-slot:table-busy>
          <div class="text-center text-danger my-2">
            <div class="loader mx-auto my-5" />
            <strong>Loading...</strong>
          </div>
        </template>
      </b-table>
      <BasePagination
        class="pagination-edit"
        v-if="totalItems > perPageCount"
        :items-count="totalItems"
        :current-page.sync="currentPage"
        :per-page-selected="perPageCount"
        items-name="Items"
        :hide-page-select="true"
      />
    </div>
    <BaseWizardFooter
      :next-text="'Done'"
      :next-icon="false"
      @next-step="onDone"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as trackersTypes from '@/modules/trackers/store/types';

import StatusBtn from '@/modules/trackers/components/trackerHealth/StatusBtn.vue';
import trackerHelper from '@/core/mixins/trackerHelper';
import textHelper from '@/core/mixins/textHelper';
import confirmBoxHelper from '@/core/mixins/confirmBoxHelper';
import EditPanel from './EditPanel.vue';


export default {
  name: 'EditTrackersModal',
  components: {
    StatusBtn,
    EditPanel,
  },
  props: {
    trackerCombination: {
      type: String,
      required: true,
      default: 'ALL',
    },
    trackerItems: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      currentPage: 1,
      perPageCount: 20,
      itemToEdit: {},
      previousItemToEdit: null,
    };
  },
  mixins: [trackerHelper, textHelper, confirmBoxHelper],
  methods: {
    ...mapActions('trackers', {
      deleteTrackers: trackersTypes.DELETE_ITEM_FROM_GROUPED_LIST,
      deleteGroupeOfTrackers: trackersTypes.DELETE_GROUP_FROM_GROUPED_LIST,
    }),
    onDone() {
      this.$root.$emit('bv::hide::modal', 'editTrackersModal');
    },
    rowActions() {
      const scope = this;
      // Edit Button Logic
      function editTrackers(item) {
        scope.itemToEdit = item;
        // eslint-disable-next-line no-underscore-dangle
        scope.$set(item, '_showDetails', !item._showDetails);
        // Check if prevItem is not null and is not current item...
        if (scope.previousItemToEdit != null && scope.previousItemToEdit?.encodedExternalId !== item.encodedExternalId) {
          // Always clsoe the previous item...
          // eslint-disable-next-line no-underscore-dangle
          scope.$set(scope.previousItemToEdit, '_showDetails', false);
        }
        // If previous item is not current item, set a new previous item...
        if (scope.previousItemToEdit?.encodedExternalId !== item.encodedExternalId) {
          scope.previousItemToEdit = item;
        }
      }

      async function deleteTrackers(trackerRow) {
        const { item } = trackerRow;
        // Confirm with user
        const confirmation = await scope.openDangerousActionConfirmBox(`Are you sure you want to delete the trackers for content item: ${item.title}`);
        if (!confirmation) return;

        // Declare data to be used later
        const { group } = trackerRow.item;
        const index = scope.trackerItems.findIndex((trackerItem) => trackerItem.contentItemUuid === trackerRow.item.contentItemUuid);
        if (index !== -1) {
          // Send Delete Trackers Request
          await scope.deleteTrackers({ groupName: group, index });
          if (!scope.trackerItems.length > 0) {
            scope.$root.$emit('bv::hide::modal', 'editTrackersModal');
          }
        }
      }

      return {
        editTrackers,
        deleteTrackers,
      };
    },
    getThirdPartyTagsInfo(item) {
      return item.imageTrackers.concat(item.scriptTrackers);
    },
  },
  computed: {
    ...mapGetters('trackers', {
      getGroupedTrackersList: trackersTypes.GET_GROUPED_TRACKERS_LIST,
    }),
    ...mapState('trackers', [
      'isImportingPlacementLevelTrackers',
    ]),
    ...mapState('content', [
      'contentItems',
    ]),
    getHeaders() {
      return [
        {
          key: 'title',
          label: 'Content Item',
          sortable: true,
          tdClass: 'text-wrap',
        },
        {
          key: 'impressionTracker',
          label: 'Impression Tags',
          sortable: false,
          tdClass: 'text-wrap',
        },
        {
          key: 'clickTracker',
          label: 'Click Tag',
          sortable: false,
          tdClass: 'text-wrap',
        },
        {
          key: 'postClickTracker',
          label: 'Post Click',
          sortable: false,
          tdClass: 'text-wrap',
        },
        {
          key: 'thirdPartyTags',
          label: '3rd Party Tags',
          sortable: false,
          tdClass: 'text-wrap',
        },
        {
          key: 'Actions',
          sortable: false,
          tdClass: 'text-wrap',
        },
      ];
    },
    contentTrackerItems() {
      // contentItems merge with trackerItems
      const contentItems = [...this.contentItems].sort((a, b) => a.title.localeCompare(b.title));
      return contentItems.map((item) => {
        const matchingTrackers = this.getGroupedTrackersList[this.trackerCombination].find((tItem) => tItem.contentItemUuid === item.encodedExternalId);
        const defaultTrackerValues = {
          impressionTracker: null,
          clickTracker: null,
          postClickTracker: null,
          thirdPartyTags: [],
          imageTrackers: [],
          scriptTrackers: [],
        };
        if (matchingTrackers) return { ...item, ...matchingTrackers };
        return { ...item, ...defaultTrackerValues };
      });
    },
    displayedTrackerItems() {
      const startingIndex = this.currentPage === 1 ? 0 : ((this.currentPage - 1) * this.perPageCount);
      const endingIndex = this.currentPage * this.perPageCount;
      return this.contentTrackerItems.slice(startingIndex, endingIndex);
    },
    totalItems() {
      return this.contentTrackerItems.length;
    },
  },
};
</script>
<style lang="scss" scoped>
.trackers-con {
  h5 {
    padding: 0 0.75rem;
  }
}
.content-tracker-table {
  max-height: 100%;
  overflow-y: auto;
}
.actions {
  .btn {
    padding: 0;
    i {
      font-size: 20px;
      // color: #adafb2;
    }

  }
}
.wrapper-edit {
  display: flex;
  flex-wrap: wrap;
  padding: 0 0.75rem;
}
.pagination-edit {
  width: 100%;
  margin: 0.75rem 0 0 0;
}
</style>
